<template>
  <Page>
    <div class="flex h-full flex-col gap-3">
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden p-6">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
              <i class="fa-solid fa-triangle-exclamation text-xl"></i>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-primary">Criticality Report</h1>
              <p class="text-secondary">Project-wide criticality matrix with dynamic topic pivot columns</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="pi pi-file-pdf"
              label="Export PDF"
              severity="secondary"
              outlined
              class="rounded-full"
              :loading="isExportingPdf"
              :disabled="isLoading || isExportingPdf || displayRows.length === 0"
              @click="handleExportPdf"
            />
            <Button
              icon="pi pi-refresh"
              label="Refresh"
              class="rounded-full"
              :loading="isLoading"
              @click="reloadReport"
            />
          </div>
        </div>
      </div>

      <div class="bg-background-card rounded-lg shadow-card overflow-hidden p-4">
        <div class="grid gap-4 xl:grid-cols-12">
          <div class="xl:col-span-8 rounded-lg border border-border bg-background-elevated p-4">
            <h2 class="text-sm font-semibold text-text">Filters</h2>
            <div class="mt-3 grid grid-cols-12 gap-3">
              <div class="col-span-12 lg:col-span-6">
                <label class="text-xs font-semibold text-text-light">Search</label>
                <InputText
                  v-model="filters.searchText"
                  class="w-full"
                  placeholder="Search asset ID or description"
                />
              </div>

              <div class="col-span-12 md:col-span-6 lg:col-span-3">
                <label class="text-xs font-semibold text-text-light">Overall Level</label>
                <MultiSelect
                  v-model="filters.overallLevelIds"
                  :options="levelOptions"
                  option-label="label"
                  option-value="value"
                  display="chip"
                  class="w-full"
                  placeholder="All levels"
                />
              </div>

              <div class="col-span-12 md:col-span-6 lg:col-span-3">
                <label class="text-xs font-semibold text-text-light">Topic Filter</label>
                <Dropdown
                  v-model="filters.topicId"
                  :options="topicOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                  show-clear
                  placeholder="Select topic"
                />
              </div>

              <div class="col-span-12 md:col-span-6">
                <label class="text-xs font-semibold text-text-light">Topic Level</label>
                <MultiSelect
                  v-model="filters.topicLevelIds"
                  :options="levelOptions"
                  option-label="label"
                  option-value="value"
                  display="chip"
                  class="w-full"
                  :disabled="!filters.topicId"
                  placeholder="All levels"
                />
              </div>

              <div class="col-span-12 md:col-span-6">
                <label class="text-xs font-semibold text-text-light">Origin</label>
                <MultiSelect
                  v-model="filters.origins"
                  :options="originOptions"
                  option-label="label"
                  option-value="value"
                  display="chip"
                  class="w-full"
                  placeholder="All origins"
                />
              </div>
            </div>
          </div>

          <div class="xl:col-span-4 rounded-lg border border-border bg-background-elevated p-4">
            <h2 class="text-sm font-semibold text-text">Sort & Actions</h2>
            <div class="mt-3 space-y-3">
              <div>
                <label class="text-xs font-semibold text-text-light">Sort By</label>
                <Dropdown
                  v-model="sortState.mode"
                  :options="sortModeOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>

              <div v-if="sortState.mode === 'topic'">
                <label class="text-xs font-semibold text-text-light">Sort Topic</label>
                <Dropdown
                  v-model="sortState.topicId"
                  :options="topicOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                  placeholder="Select topic"
                />
              </div>

              <div>
                <label class="text-xs font-semibold text-text-light">Direction</label>
                <Dropdown
                  v-model="sortState.direction"
                  :options="sortDirectionOptions"
                  option-label="label"
                  option-value="value"
                  class="w-full"
                />
              </div>
            </div>

            <div class="mt-4 flex flex-wrap items-center justify-between gap-2">
              <span class="text-xs text-text-light">
                {{ displayRows.length }} {{ displayRows.length === 1 ? 'asset' : 'assets' }}
              </span>
              <Button
                icon="pi pi-filter-slash"
                label="Clear Filters"
                severity="secondary"
                outlined
                class="rounded-full"
                :disabled="!hasActiveFilters"
                @click="resetFilters"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="relative flex min-h-80 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card">
        <div v-if="isLoading" class="flex flex-1 items-center justify-center px-6 py-8 text-sm text-text-light">
          Loading criticality report...
        </div>

        <div v-else-if="error" class="flex flex-1 items-center justify-center px-6 py-8 text-center text-sm text-error">
          {{ error }}
        </div>

        <div v-else-if="displayRows.length === 0" class="flex flex-1 items-center justify-center px-6 py-10">
          <div class="max-w-md text-center">
            <h2 class="text-base font-semibold text-text">
              {{ hasActiveFilters ? 'No assets match the current filters' : 'No report data available' }}
            </h2>
            <p class="mt-1 text-sm text-text-light">
              {{ hasActiveFilters ? 'Try adjusting your filters.' : 'Criticality report rows were not returned for this project.' }}
            </p>
          </div>
        </div>

        <div v-else class="flex-1 overflow-hidden">
          <DataTable
            :value="displayRows"
            data-key="assetId"
            scrollable
            scroll-height="flex"
            striped-rows
            show-gridlines
            class="p-datatable-sm criticality-report-table"
            :row-class="rowClass"
          >
            <Column field="assetId" header="Asset ID" style="min-width: 10rem">
              <template #body="{ data }">
                <span class="font-semibold">{{ data.assetId || '—' }}</span>
              </template>
            </Column>

            <Column field="assetDescription" header="Description" style="min-width: 16rem">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <span>{{ data.assetDescription || '—' }}</span>
                </div>
              </template>
            </Column>

            <Column header="Overall" style="min-width: 10rem">
              <template #body="{ data }">
                <Tag
                  v-if="data.overallScore?.levelId != null"
                  :value="getOverallLevelDescription(data)"
                  :severity="getLevelSeverity(data.overallScore?.levelId)"
                />
                <span v-else class="text-text-light">—</span>
              </template>
            </Column>

            <Column header="Overall Contributor" style="min-width: 12rem">
              <template #body="{ data }">
                <span :class="{ 'text-text-light': !data.overallScore?.topicDescription }">
                  {{ data.overallScore?.topicDescription || '—' }}
                </span>
              </template>
            </Column>

            <Column
              v-for="topic in topicColumns"
              :key="topic.topicId"
              :header="topic.topicDescription"
              style="min-width: 10rem"
            >
              <template #body="{ data }">
                <button
                  type="button"
                  class="topic-cell-btn"
                  :class="{ 'topic-cell-btn--driving': isDrivingTopic(data, topic.topicId) }"
                  :disabled="!getTopicScore(data, topic.topicId)"
                  @click="openTopicDetail(data, topic)"
                >
                  <Tag
                    v-if="getTopicScore(data, topic.topicId)?.scoreLevelId != null"
                    :value="getTopicLevelDescription(data, topic.topicId)"
                    :severity="getLevelSeverity(getTopicScore(data, topic.topicId)?.scoreLevelId)"
                  />
                  <span v-else class="text-text-light">—</span>
                  <i
                    v-if="isDrivingTopic(data, topic.topicId)"
                    class="fa-solid fa-bullseye text-xs text-primary"
                    title="Driving topic for overall score"
                  ></i>
                </button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <Dialog
        v-model:visible="detailDialogVisible"
        modal
        :header="detailDialogTitle"
        :style="{ width: '38rem' }"
      >
        <div v-if="selectedDetail" class="space-y-2 text-sm">
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Level</span>
            <span>{{ getLevelLabel(selectedDetail.score.scoreLevelId, selectedDetail.score.scoreLevelDescription) }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Likelihood</span>
            <span>{{ selectedDetail.score.likelihoodDescription || selectedDetail.score.likelihoodId || '—' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Consequence</span>
            <span>{{ selectedDetail.score.consequenceDescription || selectedDetail.score.consequenceId || '—' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Impact</span>
            <span>{{ selectedDetail.score.impact ?? '—' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Notes</span>
            <span>{{ selectedDetail.score.notes || '—' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] gap-2">
            <span class="text-secondary font-semibold">Origin</span>
            <span>{{ selectedDetail.score.origin || '—' }}</span>
          </div>
        </div>
      </Dialog>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import type { AssetCriticalityReportDto } from '@/shared/services/assetCriticalities';
import { addToast } from '@/shared/utils/toastMessages';
import type {
  CriticalityReportProfileFilter,
  CriticalityReportSortDirection,
  CriticalityReportSortMode,
  CriticalityReportTopicColumn,
  CriticalityReportOrigin,
} from '../composables/useCriticalityReport';
import { useCriticalityReport } from '../composables/useCriticalityReport';
import { exportCriticalityReportPdf } from '../services/criticalityReportPdf';

definePage({
  meta: {
    title: 'Criticality Report',
  },
});

type TopicScore = NonNullable<NonNullable<AssetCriticalityReportDto['topicScores']>[number]>;

interface TopicDetailSelection {
  row: AssetCriticalityReportDto;
  topic: CriticalityReportTopicColumn;
  score: TopicScore;
}

const {
  filters,
  sortState,
  topicColumns,
  levelOptions,
  displayRows,
  isLoading,
  error,
  hasActiveFilters,
  load,
  resetFilters,
  getTopicScore,
  getTopicLevelDescription,
  getOverallLevelDescription,
  getLevelLabel,
  getLevelSeverity,
} = useCriticalityReport();
const toast = useToast();

const profileStateOptions: { label: string; value: CriticalityReportProfileFilter }[] = [
  { label: 'All Profiles', value: 'all' },
  { label: 'Active Profiles', value: 'active' },
  { label: 'Disabled Profiles', value: 'disabled' },
];

const originOptions: { label: string; value: CriticalityReportOrigin }[] = [
  { label: 'Manual', value: 'Manual' },
  { label: 'Inherited', value: 'Inherited' },
];

const sortModeOptions: { label: string; value: CriticalityReportSortMode }[] = [
  { label: 'Asset ID', value: 'assetId' },
  { label: 'Overall Criticality', value: 'overall' },
  { label: 'Topic Score', value: 'topic' },
];

const sortDirectionOptions: { label: string; value: CriticalityReportSortDirection }[] = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
];

const topicOptions = computed(() =>
  topicColumns.value.map((topic) => ({
    label: topic.topicDescription,
    value: topic.topicId,
  }))
);

const detailDialogVisible = ref(false);
const selectedDetail = ref<TopicDetailSelection | null>(null);
const isExportingPdf = ref(false);

const detailDialogTitle = computed(() => {
  if (!selectedDetail.value) {
    return 'Criticality Detail';
  }

  const assetId = selectedDetail.value.row.assetId || 'Unknown Asset';
  return `${selectedDetail.value.topic.topicDescription} Assessment — ${assetId}`;
});

watch(
  topicOptions,
  (options) => {
    const hasSortTopic = options.some((option) => option.value === sortState.value.topicId);
    if (sortState.value.mode === 'topic' && !hasSortTopic) {
      sortState.value.topicId = options[0]?.value ?? null;
    }

    const hasFilterTopic = options.some((option) => option.value === filters.value.topicId);
    if (filters.value.topicId && !hasFilterTopic) {
      filters.value.topicId = null;
      filters.value.topicLevelIds = [];
    }
  },
  { immediate: true }
);

watch(
  () => sortState.value.mode,
  (mode) => {
    if (mode === 'topic' && !sortState.value.topicId) {
      sortState.value.topicId = topicOptions.value[0]?.value ?? null;
    }
  }
);

watch(
  () => filters.value.topicId,
  (topicId) => {
    if (!topicId) {
      filters.value.topicLevelIds = [];
    }
  }
);

function reloadReport(): void {
  void load();
}

async function handleExportPdf(): Promise<void> {
  if (isLoading.value) {
    addToast(toast, {
      severity: 'warn',
      summary: 'Report is loading',
      detail: 'Please wait for the report to finish loading before exporting.',
    });
    return;
  }

  if (displayRows.value.length === 0) {
    addToast(toast, {
      severity: 'warn',
      summary: 'No Data',
      detail: 'There are no report rows to export.',
    });
    return;
  }

  if (isExportingPdf.value) {
    return;
  }

  isExportingPdf.value = true;

  try {
    await exportCriticalityReportPdf({
      rows: displayRows.value,
      topicColumns: topicColumns.value,
      filters: filters.value,
      sortState: sortState.value,
      levelOptions: levelOptions.value,
      topicOptions: topicOptions.value,
      profileStateOptions,
      sortModeOptions,
      sortDirectionOptions,
      getOverallLevelDescription,
      getTopicLevelDescription,
      reportTitle: 'Criticality Report',
    });

    addToast(toast, {
      severity: 'success',
      summary: 'Export Complete',
      detail: `${displayRows.value.length} asset(s) exported to PDF.`,
    });
  } catch (exportError) {
    addToast(toast, {
      severity: 'error',
      summary: 'Export Failed',
      detail:
        exportError instanceof Error
          ? exportError.message
          : 'Failed to export the report to PDF. Please try again.',
    });
  } finally {
    isExportingPdf.value = false;
  }
}

function rowClass(_row: AssetCriticalityReportDto): string {
  return '';
}

function isDrivingTopic(row: AssetCriticalityReportDto, topicId: string): boolean {
  return row.overallScore?.topicId === topicId;
}

function openTopicDetail(row: AssetCriticalityReportDto, topic: CriticalityReportTopicColumn): void {
  const score = getTopicScore(row, topic.topicId);
  if (!score) {
    return;
  }

  selectedDetail.value = { row, topic, score };
  detailDialogVisible.value = true;
}

onMounted(() => {
  void load();
});
</script>

<style scoped>
@reference '../../../style.css';
.topic-cell-btn {
  @apply inline-flex items-center gap-1 rounded px-1 py-0.5 transition hover:bg-primary/5 disabled:cursor-default disabled:hover:bg-transparent;
}

.topic-cell-btn--driving {
  @apply ring-1 ring-primary/30;
}

:deep(.p-datatable-tbody > tr.criticality-report-row-disabled > td) {
  @apply text-text-light;
  background-color: rgb(var(--color-secondary) / 0.08);
}

:deep(.p-datatable-thead > tr > th) {
  @apply bg-background-elevated text-xs font-semibold uppercase tracking-wide text-text-light;
}
</style>
